import React, { useEffect, useState } from 'react';
import { X, Edit, FileText, Shirt } from 'lucide-react';
import api from '../services/api';
import StudentForm from './StudentForm';
import toast from 'react-hot-toast';

export default function StudentDetail({ studentId, onClose, onUpdated }) {
  const [student, setStudent] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    api.get(`/students/${studentId}`).then(r => setStudent(r.data)).catch(console.error);
  }, [studentId]);

  if (!student) return null;

  const activeTenancy = student.tenancies?.find(t => t.status === 'ACTIVE');

  const Row = ({ label, value }) => value ? (
    <div className="flex justify-between py-1.5 border-b border-gray-50">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs text-gray-800 font-medium text-right max-w-[60%]">{value}</span>
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h3 className="font-bold text-lg">{student.firstName} {student.lastName}</h3>
            <p className="text-brand-600 text-sm font-mono">{student.registrationNo}</p>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowEdit(true)} className="text-gray-500 hover:text-brand-700 p-2"><Edit size={18} /></button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2"><X size={18} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Personal Information</h4>
            <Row label="Phone" value={student.phone} />
            <Row label="Email" value={student.email} />
            <Row label="NIC" value={student.nicNumber} />
            <Row label="Date of Birth" value={student.dateOfBirth ? new Date(student.dateOfBirth).toLocaleDateString() : null} />
            <Row label="Address" value={student.address} />
            <Row label="University" value={student.university} />
            <Row label="Course" value={student.course} />
            <Row label="Academic Year" value={student.academicYear} />
          </div>

          {activeTenancy && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Room</h4>
              <Row label="Room" value={activeTenancy.room?.roomNumber} />
              <Row label="Monthly Rent" value={`Rs. ${Number(activeTenancy.monthlyRent).toLocaleString()}`} />
              <Row label="Key Money" value={`Rs. ${Number(activeTenancy.keyMoney).toLocaleString()}`} />
              <Row label="Start Date" value={new Date(activeTenancy.startDate).toLocaleDateString()} />
            </div>
          )}

          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Family Details</h4>
            <Row label="Father" value={student.fatherName} />
            <Row label="Father Phone" value={student.fatherPhone} />
            <Row label="Mother" value={student.motherName} />
            <Row label="Mother Phone" value={student.motherPhone} />
            <Row label="Emergency Contact" value={student.emergencyContact} />
            <Row label="Emergency Phone" value={student.emergencyPhone} />
          </div>

          {(student.guardianName || student.guardianPhone) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Guardian</h4>
              <Row label="Name" value={student.guardianName} />
              <Row label="Phone" value={student.guardianPhone} />
              <Row label="Relationship" value={student.guardianRelation} />
            </div>
          )}

          {(student.studentIdDoc || student.nicDoc || student.photoDoc) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Documents</h4>
              <div className="flex flex-wrap gap-2">
                {[['studentIdDoc', 'Student ID'], ['nicDoc', 'NIC'], ['fatherNicDoc', 'Father NIC'], ['motherNicDoc', 'Mother NIC'], ['guardianNicDoc', 'Guardian NIC'], ['photoDoc', 'Photo']].map(([field, label]) =>
                  student[field] ? (
                    <a key={field} href={`http://localhost:5010/uploads/documents/${student[field]}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 text-xs bg-brand-50 text-brand-700 px-3 py-1.5 rounded-full hover:bg-brand-100">
                      <FileText size={12} /> {label}
                    </a>
                  ) : null
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showEdit && <StudentForm initial={student} onClose={() => setShowEdit(false)} onSaved={() => { setShowEdit(false); api.get(`/students/${studentId}`).then(r => setStudent(r.data)); onUpdated && onUpdated(); }} />}
    </div>
  );
}
